/**
 * SC-209: the glyphs the floating window draws.
 *
 * Deliberately not MUI icons. Those are emotion components, which insert their rules into whichever head
 * their cache points at, and getting that wrong in a second document shows up as an invisible button rather
 * than an error. A bare `<svg>` with `currentColor` has nothing to get wrong. The paths are the Material
 * Design ones the rest of the app already uses, so the two toolbars look like the same product.
 */

import React from 'react'

interface GlyphProps {
  size?: number
  style?: React.CSSProperties
  className?: string
}

function Glyph({ size = 16, style, className, d }: GlyphProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      style={style}
      className={className}
    >
      <path d={d} />
    </svg>
  )
}

const MIC =
  'M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z'

const MIC_OFF =
  'M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z'

const CAMERA = 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z'

const CAMERA_OFF =
  'M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z'

const BACK_TO_TAB =
  'M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z'

const CLOSE =
  'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'

const SPEAKER =
  'M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z'

/** Material's `picture_in_picture_alt` — the same glyph the tab's pop-out button uses. */
const AUTO_OPEN =
  'M19 11h-8v6h8v-6zm4 8V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2zm-2 .02H3V4.97h18v14.05z'

/** Material's `cancel_presentation` — a screen with a cross, for a window that will not open itself. */
const AUTO_OPEN_OFF =
  'M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16.02H3V4.98h18v14.04zM14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41z'

/** Material's `warning_amber_rounded` - the glyph on the call bar's background warning (SC-158). */
const WARNING =
  'M12 5.99 19.53 19H4.47zM2.74 18c-.77 1.33.19 3 1.73 3h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0zM11 11v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1m0 5h2v2h-2z'

export const MicGlyph = (props: GlyphProps) => <Glyph {...props} d={MIC} />
export const MicOffGlyph = (props: GlyphProps) => <Glyph {...props} d={MIC_OFF} />
export const CameraGlyph = (props: GlyphProps) => <Glyph {...props} d={CAMERA} />
export const CameraOffGlyph = (props: GlyphProps) => <Glyph {...props} d={CAMERA_OFF} />
export const BackToTabGlyph = (props: GlyphProps) => <Glyph {...props} d={BACK_TO_TAB} />
export const CloseGlyph = (props: GlyphProps) => <Glyph {...props} d={CLOSE} />
export const SpeakerGlyph = (props: GlyphProps) => <Glyph {...props} d={SPEAKER} />
export const AutoOpenGlyph = (props: GlyphProps) => <Glyph {...props} d={AUTO_OPEN} />
export const AutoOpenOffGlyph = (props: GlyphProps) => <Glyph {...props} d={AUTO_OPEN_OFF} />
export const WarningGlyph = (props: GlyphProps) => <Glyph {...props} d={WARNING} />
